import {
  AuthenticateTowerPaymentsResult,
  GlobalSettings,
  PairedTowerPaymentsDevice,
} from "@towersystems/roam-common/lib/generated-types";
import { PaymentScreenProps } from "../../PaymentScreen";
import { Spacings, View } from "react-native-ui-lib";
import {
  Button,
  LoadingIndicator,
  Stack,
  Text,
} from "@towersystems/roam-common-native";
import React, { useState } from "react";
import { usePaymentControllerProvider } from "../../../../utils/payment-controller";
import colors from "react-native-ui-lib/src/style/colors";
import PaymentMethod from "../../../../../../utilities/data/model/PaymentMethod";
import Terminal from "../../../../../../utilities/data/model/Terminal";
import Sale from "../../../../../../utilities/data/model/Sale";
import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import {
  MUTATION_AUTHENTICATE_TOWER_PAYMENTS_BY_METHOD_ID,
  QUERY_PAIRED_TOWER_PAYMENTS_DEVICE,
} from "../../../../../../common/queries";
import {
  createTowerPaymentsSDK,
  Transaction,
  TransactionType,
} from "../../../../../../utilities/tower-payments";
import {
  clearTowerPaymentsSession,
  retrieveTowerPaymentSession,
  saveTowerPaymentsSession,
} from "../../../../../../utilities/tower-payments/utils";
import { toHtmlPage } from "../../../../../../utilities/to-html-page";
import { AddPaymentOptions } from "../../../../utils/payment-controller/types";

export interface MakeTowerPaymentProps {
  paymentMethod: PaymentMethod;
  currentTerminal: Terminal;
  globalSettings: GlobalSettings;
  sale: Sale;
  navigation: PaymentScreenProps["navigation"];
}

export const MakeTowerPayment = ({
  navigation,
  paymentMethod,
  sale,
}: MakeTowerPaymentProps) => {
  const [pairedDevice, setPairedDevice] = React.useState<
    PairedTowerPaymentsDevice | undefined
  >(undefined);

  const [transactionResult, setTransactionResult] =
    React.useState<Transaction | null>(null);

  const [showRecoveryButton, setShowRecoveryButton] =
    React.useState<boolean>(false);

  const [showCompletedButton, setShowCompletedButton] =
    React.useState<boolean>(false);

  const [displayText, setDisplayText] = React.useState<string[][]>([
    ["Initializing Tower Payment..."],
  ]);

  const token = React.useRef<string | null>(null);
  const [errorText, setErrorText] = React.useState<string | null>(null);

  const [showLoading] = React.useState<boolean>(false);

  const [authenticate] = useMutation<{
    authenticateTowerPaymentsByMethodId: AuthenticateTowerPaymentsResult;
  }>(MUTATION_AUTHENTICATE_TOWER_PAYMENTS_BY_METHOD_ID);

  const [queryPairedDevice] = useLazyQuery<{
    pairedTowerPaymentsDeviceByMethodId: PairedTowerPaymentsDevice | null;
  }>(QUERY_PAIRED_TOWER_PAYMENTS_DEVICE);

  const intervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const handledRef = React.useRef<boolean>(false); // <-- our guard

  const {
    validTenderedAmount,
    addPayment,
    tenderedAmountAsInteger,
    isRefundAmount,
  } = usePaymentControllerProvider();

  React.useEffect(() => {
    navigation.setOptions({
      cardStyle: {
        backgroundColor: isRefundAmount ? colors.tyroRefund : colors.tyro,
      },
      headerStyle: {
        backgroundColor: isRefundAmount ? colors.tyroRefund : colors.tyro,
        borderBottomColor: "rgba(255,255,255,0.2)",
      },
      headerTitle: () => null,
    });
  }, [colors, isRefundAmount]);

  React.useEffect(() => {
    (async () => {
      let pairedDevice: PairedTowerPaymentsDevice | undefined;
      try {
        pairedDevice = await fetchPairedDevice(paymentMethod.id);
        if (!pairedDevice) {
          setDisplayText([["Please pair a device."]]);
          return;
        }

        if (!token.current) {
          const { data } = await authenticate({
            variables: {
              paymentMethodId: paymentMethod.id,
            },
          });
          if (
            data?.authenticateTowerPaymentsByMethodId &&
            "token" in data.authenticateTowerPaymentsByMethodId
          ) {
            token.current = data.authenticateTowerPaymentsByMethodId.token;
          } else {
            setErrorText(
              "Failed to authenticate Tower Payments device. Check pairing."
            );
            return;
          }
        }
      } catch (error) {
        setErrorText("Error fetching paired device details");
        return;
      }

      const maybeExistingSession = await retrieveTowerPaymentSession({
        saleId: sale.id,
      });

      if (maybeExistingSession) {
        setDisplayText(() => [
          [
            "Failed to retrieve a response from Tower Payments for last transaction.",
            "Attempt recovery?",
          ],
        ]);
        setShowRecoveryButton(true);
        return;
      }

      if (token.current) {
        await beginTransaction(token.current);
      }
    })();
  }, []);

  React.useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;

    // Prevent starting a new interval if one is already running
    if (intervalRef.current) {
      return; // already polling!
    }

    if (transactionResult && pairedDevice && !handledRef.current) {
      const sdk = createTowerPaymentsSDK({
        token: token.current ?? "",
      });
      setDisplayText((prev) => [
        ...prev,
        [
          `Polling ${pairedDevice.deviceDescription} for transaction outcome...`,
        ],
      ]);

      // Set a timeout to update text after 30 seconds
      timeout = setTimeout(() => {
        setDisplayText((prev) => [
          ...prev,
          ["Polling for result taking longer than expected. Please wait."],
        ]);
      }, 30000);

      intervalRef.current = setInterval(() => {
        sdk
          .pollOutcome(transactionResult.id)
          .then(async (result) => {
            console.log("Polling result:", result);
            if (handledRef.current) {
              if (intervalRef.current) clearInterval(intervalRef.current);
              clearTimeout(timeout); // clear the timeout
              intervalRef.current = null;

              return;
            }

            if (result.status === "PENDING") {
              // do nothing, continue polling
              return;
            } else if (result.status === "COMPLETED") {
              handledRef.current = true;
              if (intervalRef.current) clearInterval(intervalRef.current);
              clearTimeout(timeout); // clear the timeout
              intervalRef.current = null;
              await handleCompletedTransactionResult(result);
            }
          })
          .catch((error) => {
            setErrorText("Failed to poll transaction outcome. Trying again.");
          });
      }, 2500);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [transactionResult, pairedDevice]);

  const handleCompletedTransactionResult = async (result: Transaction) => {
    if (result.state !== "PaymentSettled") {
      await clearTowerPaymentsSession();
      if (result.state === "Voided") {
        setErrorText("Payment was voided.");
      } else {
        setErrorText("Payment was not successful.");
      }
      return;
    } else if (result.state === "PaymentSettled") {
      setDisplayText((prev) => [...prev, ["Payment completed successfully."]]);
      await addSalePayment(result);
    }
  };

  const addSalePayment = React.useCallback(
    async (response: Transaction) => {
      try {
        if (
          response.status !== "COMPLETED" ||
          response.state !== "PaymentSettled"
        ) {
          setErrorText("Something went wrong.");
          return;
        }

        const transactionType = response.transactionType;

        if (
          false ==
          [TransactionType.PURCHASE, TransactionType.REFUND].includes(
            transactionType
          )
        ) {
          setErrorText(
            `Transaction type "${transactionType}" is not supported.`
          );
          return;
        }

        const purchaseAmountAsInteger = response.paymentsTotalInCents;

        const purchaseAmountAsDollars = purchaseAmountAsInteger / 100;

        const maybeCustomerReceipt =
          response.payments.length >= 1
            ? response.payments[0].receipt
            : undefined;

        const maybeSurchargeAmount =
          transactionType === TransactionType.PURCHASE
            ? response.paymentsSurchargeTotalInCents / 100
            : undefined;

        const paymentAmount =
          transactionType === TransactionType.REFUND
            ? -Math.abs(purchaseAmountAsDollars)
            : purchaseAmountAsDollars;

        const paymentInputOptions: AddPaymentOptions = {
          tenderedAmount: paymentAmount,
          surcharge: maybeSurchargeAmount,
          name: "Tower Payments",
          transactionId: response.id,
          metadata: {
            response,
            customerReceipt: maybeCustomerReceipt
              ? toHtmlPage(maybeCustomerReceipt)
              : undefined,
          },
        };

        await addPayment(paymentMethod, paymentInputOptions);
        await clearTowerPaymentsSession();
        setShowCompletedButton(true);
      } catch (error) {
        setErrorText("Failed to add payment. Check terminal for correctness.");
      }
    },
    [sale]
  );

  const fetchPairedDevice = async (
    paymentMethodId: string
  ): Promise<PairedTowerPaymentsDevice | undefined> => {
    const response = await queryPairedDevice({
      variables: { paymentMethodId: paymentMethodId },
    });

    const device = response.data?.pairedTowerPaymentsDeviceByMethodId;

    if (device) {
      setPairedDevice(device);
      return device;
    }
    return undefined;
  };

  const beginTransaction = async (token: string) => {
    if (validTenderedAmount && tenderedAmountAsInteger) {
      try {
        const sdk = createTowerPaymentsSDK({ token });

        const result = await sdk.startTransaction(
          Math.abs(tenderedAmountAsInteger),
          isRefundAmount ? TransactionType.REFUND : TransactionType.PURCHASE
        );

        if ("id" in result) {
          await saveTowerPaymentsSession({
            saleId: sale.id,
            transaction: result,
          });
          setTransactionResult(result);
        } else {
          setErrorText("Failed to start transaction.");
          return;
        }
      } catch (err) {
        if (err instanceof ApolloError) {
          const originalErrorCode = err.graphQLErrors[0]?.extensions?.code;
          if (originalErrorCode === "FORBIDDEN") {
            setErrorText("Invalid pairing. Please re-pair payment device.");
            return;
          }
        }
        setErrorText("Unknown error occurred. Unable to start transaction.");
      }
    }
  };

  const recoveryButtonMarkup = showRecoveryButton ? (
    <>
      <Button
        variant="outline"
        style={{ minWidth: 120 }}
        onPress={async () => {
          if (!token.current) {
            setErrorText("No paired device found.");
            return;
          }
          await clearTowerPaymentsSession();
          setDisplayText([]);
          setShowRecoveryButton(false);
          await beginTransaction(token.current);
        }}
      >
        <Text
          style={{ color: "#fff", fontFamily: "Gilmer-Bold", fontSize: 17 }}
        >
          New Transaction
        </Text>
      </Button>
      <Button
        variant="outline"
        style={{ minWidth: 120 }}
        onPress={async () => {
          const maybeExistingSession = await retrieveTowerPaymentSession({
            saleId: sale.id,
          });
          if (maybeExistingSession) {
            setDisplayText((prev) => [
              ...prev,
              ["Attempting to recover previous transaction..."],
            ]);
            setShowRecoveryButton(false);
            setTransactionResult(maybeExistingSession);
          }
        }}
      >
        <Text
          style={{ color: "#fff", fontFamily: "Gilmer-Bold", fontSize: 17 }}
        >
          Retry Transaction
        </Text>
      </Button>
    </>
  ) : null;

  const onDonePressed = async () => {
    const paidOff = await sale.balancePaidOff_ASYNC();

    if (paidOff) {
      navigation.navigate("CheckoutSummaryRootScreen", {
        screen: "CheckoutReviewScreen",
      });
    } else {
      navigation.navigate("SelectPaymentMethodScreen");
    }
  };

  const showCompletedButtonMarkup = showCompletedButton ? (
    <Button
      variant="outline"
      style={{ minWidth: 120 }}
      onPress={async () => {
        onDonePressed();
      }}
    >
      <Text style={{ color: "#fff", fontFamily: "Gilmer-Bold", fontSize: 17 }}>
        Done
      </Text>
    </Button>
  ) : null;

  const errorButtonMarkup = errorText ? (
    <Button
      variant="outline"
      style={{ minWidth: 120 }}
      onPress={() => navigation.goBack()}
    >
      <Text style={{ color: "#fff", fontFamily: "Gilmer-Bold", fontSize: 17 }}>
        Go back
      </Text>
    </Button>
  ) : null;

  const btnMarkup = (recoveryButtonMarkup ||
    errorButtonMarkup ||
    showCompletedButtonMarkup) && (
    <View style={{ marginTop: Spacings.s4 }}>
      <Stack centerHorizontal>
        {errorButtonMarkup}
        {recoveryButtonMarkup}
        {showCompletedButtonMarkup}
      </Stack>
    </View>
  );

  const errorTextMarkup = errorText ? (
    <Text
      style={{
        textAlign: "center",
        fontSize: 18,
        marginBottom: Spacings.s6,
        color: colors.white,
      }}
    >
      {errorText}
    </Text>
  ) : null;

  const displayTextMarkup =
    displayText.length >= 1 &&
    displayText[displayText.length - 1].map((text, index) => (
      <Text
        style={{
          textAlign: "center",
          fontSize: 18,
          marginBottom: Spacings.s6,
          color: colors.white,
        }}
        key={index}
      >
        {text}
      </Text>
    ));

  const loadingMarkup =
    showLoading || (!btnMarkup && !displayTextMarkup && !errorTextMarkup) ? (
      <LoadingIndicator />
    ) : undefined;

  return (
    <View
      flex
      style={{
        width: "100%",
        height: "100%",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <View style={{ justifyContent: "center", alignItems: "center" }}>
        {loadingMarkup}
        {errorTextMarkup || displayTextMarkup}
        {btnMarkup}
      </View>
    </View>
  );
};
